import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Flex, Typography, Card, Tabs } from 'antd';
import {
  CalculatorOutlined,
  CarOutlined,
  BuildOutlined,
  ImportOutlined,
} from '@ant-design/icons';
import GeorgianCustomsCalculator from '../../components/tools/GeorgianCustomsCalculator';
import VehicleImportCalculator from '../../components/tools/VehicleImportCalculator';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/TranslationContext';

const ToolsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('0');

  const calculatorInitialQuery = useMemo(() => {
    const keys = [
      'auction',
      'price',
      'year',
      'month',
      'day',
      'engineVol',
      'engineType',
      'carScale',
      'carType',
      'enableInsurance',
      'isJuridical',
    ];
    const initialQuery = {};

    keys.forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') {
        initialQuery[key] = value;
      }
    });

    return initialQuery;
  }, [searchParams]);

  const getUserCountry = () => {
    if (user?.branchCountry) {
      return user.branchCountry === 'georgia' ? 'ge' : 'am';
    }
    return 'am';
  };

  const tools = [
    {
      id: 'vehicle-import-calculator',
      key: '0',
      name: t('toolsPage.vehicleImportCalculator', 'Vehicle Import Calculator'),
      icon: <ImportOutlined />,
      component: (
        <VehicleImportCalculator
          userCountry={getUserCountry()}
          fromDashboard={true}
          initialQuery={calculatorInitialQuery}
        />
      ),
      description: t(
        'toolsPage.vehicleImportDesc',
        'Calculate total import costs for vehicles from IAAI and Copart auctions. Supports Armenia and Georgia with country-specific tax calculations and user discounts.'
      ),
    },
    {
      id: 'georgian-customs-calculator',
      key: '1',
      name: t('toolsPage.georgianCustomsCalculator', 'Georgian Customs Calculator'),
      icon: <CarOutlined />,
      component: <GeorgianCustomsCalculator />,
      description: t(
        'toolsPage.georgianCustomsDesc',
        'Calculate Georgian customs fees for vehicle registration including excise tax, import tax, and registration fees.'
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Flex vertical gap={16} style={{ marginBottom: 32 }}>
        <Typography.Title level={3} style={{ margin: 0, fontWeight: 700 }}>
          {t('toolsPage.title', 'Tools & Calculators')}
        </Typography.Title>
        <Typography.Text type="secondary">
          {t('toolsPage.subtitle', 'Useful tools and calculators for managing your auction business')}
        </Typography.Text>
      </Flex>

      <Card style={{ marginBottom: 24 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={tools.map((tool) => ({
            key: tool.key,
            label: (
              <Flex align="center" gap={8}>
                {tool.icon}
                {tool.name}
              </Flex>
            ),
            children: (
              <Flex vertical gap={24} style={{ marginTop: 24 }}>
                <Card
                  style={{
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)',
                    color: 'white',
                  }}
                >
                  <Flex align="center" gap={16}>
                    <div style={{ fontSize: 40 }}>{tool.icon}</div>
                    <Flex vertical>
                      <Typography.Title level={5} style={{ margin: 0, color: 'white' }}>
                        {tool.name}
                      </Typography.Title>
                      <Typography.Text style={{ opacity: 0.9, color: 'white' }}>
                        {tool.description}
                      </Typography.Text>
                    </Flex>
                  </Flex>
                </Card>
                <div>{tool.component}</div>
              </Flex>
            ),
          }))}
        />
      </Card>

      {tools.length === 0 && (
        <Flex vertical gap={16} style={{ marginTop: 32 }}>
          <Typography.Title level={5}>{t('toolsPage.comingSoon', 'Coming Soon')}</Typography.Title>
          <Flex gap={24} wrap="wrap">
            <Card style={{ flex: 1, minWidth: 200, opacity: 0.6 }}>
              <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
                <CalculatorOutlined style={{ fontSize: 40, color: 'var(--colorPrimary)' }} />
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t('toolsPage.profitCalculator', 'Profit Calculator')}
                </Typography.Title>
              </Flex>
              <Typography.Text type="secondary">
                {t('toolsPage.profitCalculatorDesc', 'Calculate profit margins and ROI for auction purchases')}
              </Typography.Text>
            </Card>
            <Card style={{ flex: 1, minWidth: 200, opacity: 0.6 }}>
              <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
                <BuildOutlined style={{ fontSize: 40, color: 'var(--colorPrimary)' }} />
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t('toolsPage.shippingCalculator', 'Shipping Calculator')}
                </Typography.Title>
              </Flex>
              <Typography.Text type="secondary">
                {t('toolsPage.shippingCalculatorDesc', 'Estimate shipping costs from auction to your location')}
              </Typography.Text>
            </Card>
          </Flex>
        </Flex>
      )}
    </div>
  );
};

export default ToolsPage;
